import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import {
  NodeUserAccessControl,
  UploadedAccessControlInfo,
  AccessControlCSVInfoResponse,
} from '../../Interfaces/AccessControl';
import { State } from '@progress/kendo-data-query';

export enum AccessControlActionTypes {
  ACCESS_CONTROL_UNAUTHORIZED_REQUEST = 'NBC_UNAUTHORIZED_REQUEST',
  RESET_ACCESS_CONTROL_HTTP_RESPONSE_FETCH_ERROR = 'RESET_ACCESS_CONTROL_HTTP_RESPONSE_FETCH_ERROR',
  RECEIVED_ACCESS_CONTROL_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_ACCESS_CONTROL_HTTP_RESPONSE_ERROR_CODE',
  REQUEST_ACCESS_CONTROL_FILE_PATH = 'REQUEST_ACCESS_CONTROL_FILE_PATH',
  SET_UPLOADED_ACCESS_CONTROL_CSV = 'SET_UPLOADED_ACCESS_CONTROL_CSV',
  SAVING_ACCESS_CONTROL_GRID_STATE = 'SAVING_ACCESS_CONTROL_GRID_STATE',
  FETCH_LATEST_ACCESS_CONTROL_CSV = 'FETCH_LATEST_ACCESS_CONTROL_CSV',
}

export interface AccessControlState {
  fetchError: FetchError;
  message: string;
  filePath: string;
  laneNumberSelectionFilter: string[];
  uploadedAccessControlInfo: UploadedAccessControlInfo;
  accessControlGridState: State;
  latestAccessControlCSV: NodeUserAccessControl[] | undefined;
  fileName: string | undefined;
}

interface ReceivedErrorCode {
  type: AccessControlActionTypes.RECEIVED_ACCESS_CONTROL_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: AccessControlActionTypes.RESET_ACCESS_CONTROL_HTTP_RESPONSE_FETCH_ERROR;
}

interface RequestAccessControlFilePath {
  type: AccessControlActionTypes.REQUEST_ACCESS_CONTROL_FILE_PATH;
  filePath: string;
  laneNumberSelectionFilter: string[];
}

interface SetUploadedAccessControlCSV {
  type: AccessControlActionTypes.SET_UPLOADED_ACCESS_CONTROL_CSV;
  uploadedAccessControlInfo: UploadedAccessControlInfo;
}

interface saveAccessControlGridState {
  type: AccessControlActionTypes.SAVING_ACCESS_CONTROL_GRID_STATE;
  accessControlGridState: State;
}

interface fetchLatestUploadCSV {
  type: AccessControlActionTypes.FETCH_LATEST_ACCESS_CONTROL_CSV;
  latestAccessControlCSV: AccessControlCSVInfoResponse;
}

type KnownAction =
  | ReceivedErrorCode
  | ResetHttpResponseError
  | RequestAccessControlFilePath
  | SetUploadedAccessControlCSV
  | saveAccessControlGridState
  | fetchLatestUploadCSV;

const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: AccessControlActionTypes.ACCESS_CONTROL_UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: AccessControlActionTypes.RECEIVED_ACCESS_CONTROL_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

export const actionCreators = {
  fetchAccessControlFilePath:
    (fileName: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AccessControlActionTypes.REQUEST_ACCESS_CONTROL_FILE_PATH}`
      );

      const response = await fetch(
        `AccessControl/GetUploadAccessControlFilePath/${fileName}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as {
        folderPath: string;
        laneNumberSelectionFilter: string[];
      };

      dispatch({
        type: AccessControlActionTypes.REQUEST_ACCESS_CONTROL_FILE_PATH,
        filePath: result.folderPath,
        laneNumberSelectionFilter: result.laneNumberSelectionFilter,
      });
    },

  fetchUploadedAccessControlCSV:
    (uploadedCSV: UploadedAccessControlInfo): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AccessControlActionTypes.SET_UPLOADED_ACCESS_CONTROL_CSV}`
      );

      dispatch({
        type: AccessControlActionTypes.SET_UPLOADED_ACCESS_CONTROL_CSV,
        uploadedAccessControlInfo: uploadedCSV,
      });
    },

  savingAccessControlGridState:
    (dataState: State): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AccessControlActionTypes.SAVING_ACCESS_CONTROL_GRID_STATE}`
      );

      dispatch({
        type: AccessControlActionTypes.SAVING_ACCESS_CONTROL_GRID_STATE,
        accessControlGridState: dataState,
      });
    },

  getLatestUploadAccessControlCSV:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AccessControlActionTypes.FETCH_LATEST_ACCESS_CONTROL_CSV}`
      );

      const response = await fetch(
        `AccessControl/GetLatestAccessControlCSV`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AccessControlCSVInfoResponse;

      dispatch({
        type: AccessControlActionTypes.FETCH_LATEST_ACCESS_CONTROL_CSV,
        latestAccessControlCSV: result,
      });
    },
};

export const initialDataState: State = {
  take: 50,
  skip: 0,
  group: [],
  sort: [],
};

export const unloadedState: AccessControlState = {
  fetchError: { StatusCode: 0, Message: '' },
  message: '',
  filePath: '',
  laneNumberSelectionFilter: [],
  uploadedAccessControlInfo: { fileInfo: [], UsersAccessControl: [] },
  accessControlGridState: initialDataState,
  latestAccessControlCSV: [],
  fileName: '',
};

export const reducer: Reducer<AccessControlState> = (
  state: AccessControlState | undefined,
  incomingAction: Action
): AccessControlState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case AccessControlActionTypes.RESET_ACCESS_CONTROL_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case AccessControlActionTypes.RECEIVED_ACCESS_CONTROL_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_NBC',
      };
      return {
        ...state,
        fetchError: fetchErrorData,
      };
    case AccessControlActionTypes.REQUEST_ACCESS_CONTROL_FILE_PATH:
      return {
        ...state,
        filePath: action.filePath,
        laneNumberSelectionFilter: action.laneNumberSelectionFilter,
      };
    case AccessControlActionTypes.SET_UPLOADED_ACCESS_CONTROL_CSV:
      return {
        ...state,
        uploadedAccessControlInfo: action.uploadedAccessControlInfo,
      };
    case AccessControlActionTypes.SAVING_ACCESS_CONTROL_GRID_STATE:
      return {
        ...state,
        accessControlGridState: action.accessControlGridState,
      };
    case AccessControlActionTypes.FETCH_LATEST_ACCESS_CONTROL_CSV:
      return {
        ...state,
        fileName:
          action.latestAccessControlCSV.UploadedAccessControlInfo?.FileName,
        latestAccessControlCSV:
          action.latestAccessControlCSV.UploadedAccessControlInfo
            ?.NodeUserAccessControlList,
      };
    default:
      break;
  }
  return state;
};
